import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, ArrowLeft, Edit, Check, X, XCircle,
  Trash2, Send, Headphones, Loader2, RefreshCw,
} from "lucide-react";

export const SupportSection = ({ chatTickets, fetchData }) => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [supportAgentName, setSupportAgentName] = useState("Customer Support");
  const [editingAgentName, setEditingAgentName] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSelectChat = async (chat) => {
    setSelectedChat(chat);
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/chat/${chat.chat_id}/messages`, { headers: getAuthHeaders(), withCredentials: true });
      setChatMessages(res.data || []);
    } catch { toast.error("Failed to load messages"); }
    finally { setLoadingMessages(false); }
  };

  const handleAdminReply = async (e) => {
    e?.preventDefault();
    if (!replyMessage.trim() || !selectedChat) return;
    setSendingReply(true);
    try {
      const res = await axios.post(`${API}/admin/chat/${selectedChat.chat_id}/reply`, {
        content: replyMessage, sender_name: supportAgentName
      }, { headers: getAuthHeaders(), withCredentials: true });
      setChatMessages((prev) => [...prev, res.data]);
      setReplyMessage("");
    } catch { toast.error("Failed to send reply"); }
    finally { setSendingReply(false); }
  };

  const handleSaveAgentName = async () => {
    try {
      await axios.put(`${API}/admin/settings/support`, { default_agent_name: supportAgentName }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Agent name saved");
      setEditingAgentName(false);
    } catch { toast.error("Failed to save"); }
  };

  const handleCloseChat = async (chatId) => {
    try {
      await axios.put(`${API}/admin/chat/${chatId}/close`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Chat closed");
      setSelectedChat(null);
      setChatMessages([]);
      fetchData();
    } catch { toast.error("Failed to close"); }
  };

  const handleDeleteChat = async (chatId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this chat and all messages?")) return;
    try {
      await axios.delete(`${API}/admin/chat/${chatId}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Chat deleted");
      if (selectedChat?.chat_id === chatId) { setSelectedChat(null); setChatMessages([]); }
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2">
          <MessageCircle className="w-5 h-5" /> Support Tickets
        </CardTitle>
        <Button variant="outline" size="sm" className="border-slate-300" onClick={() => { setSelectedChat(null); setChatMessages([]); fetchData(); }}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {selectedChat ? (
          <div className="flex flex-col h-[600px]" data-testid="admin-chat-view">
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-slate-200 bg-slate-50">
              <button onClick={() => { setSelectedChat(null); setChatMessages([]); }} className="text-slate-600 hover:text-slate-800">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1">
                <p className="font-medium text-slate-700 text-sm">{selectedChat.user_name || "Guest"}</p>
                <p className="text-xs text-slate-500">{selectedChat.user_email || selectedChat.guest_id || "No email"}</p>
              </div>
              <div className="flex items-center gap-2">
                {editingAgentName ? (
                  <div className="flex items-center gap-1">
                    <Input value={supportAgentName} onChange={(e) => setSupportAgentName(e.target.value)} className="h-7 w-36 text-xs" data-testid="agent-name-input" />
                    <button onClick={handleSaveAgentName} className="text-emerald-600"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingAgentName(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => setEditingAgentName(true)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs hover:bg-slate-200" data-testid="edit-agent-name-btn">
                    <Edit className="w-3 h-3" /><span>{supportAgentName}</span>
                  </button>
                )}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedChat.status === "open" || selectedChat.status === "pending" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                {selectedChat.status}
              </span>
              {(selectedChat.status === "open" || selectedChat.status === "pending") && (
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50 text-xs h-7" onClick={() => handleCloseChat(selectedChat.chat_id)} data-testid="close-chat-btn">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Close
                </Button>
              )}
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50 text-xs h-7" onClick={(e) => handleDeleteChat(selectedChat.chat_id, e)} data-testid="delete-chat-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {loadingMessages ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
              ) : chatMessages.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No messages yet</p>
              ) : chatMessages.map((msg) => (
                <div key={msg.message_id} className={`flex ${msg.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] rounded-2xl p-3 ${msg.sender_type === "admin" ? "bg-slate-700 text-white rounded-br-md" : "bg-white border border-slate-200 rounded-bl-md"}`}>
                    <p className={`text-xs font-medium mb-1 ${msg.sender_type === "admin" ? "text-slate-300" : "text-slate-600"}`}>
                      {msg.sender_type === "admin" ? (msg.sender_name || supportAgentName) : (msg.sender_name || "User")}
                    </p>
                    {msg.attachment_url && msg.attachment_type === "image" && (
                      <img src={msg.attachment_url} alt="Attachment" className="max-w-full rounded-lg mb-2 max-h-48" />
                    )}
                    <p className={`text-sm ${msg.sender_type === "admin" ? "" : "text-slate-700"}`}>{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.sender_type === "admin" ? "text-slate-400" : "text-slate-400"}`}>
                      {new Date(msg.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply */}
            {(selectedChat.status === "open" || selectedChat.status === "pending") && (
              <form onSubmit={handleAdminReply} className="p-3 border-t border-slate-200 bg-white flex items-center gap-2 shrink-0">
                <Input placeholder="Type your reply..." value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} className="flex-1" disabled={sendingReply} data-testid="admin-reply-input" />
                <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={sendingReply || !replyMessage.trim()} data-testid="admin-reply-send-btn">
                  {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="admin-tickets-list">
            {chatTickets.length === 0 ? (
              <div className="text-center py-12">
                <Headphones className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No support tickets yet</p>
              </div>
            ) : chatTickets.map((ticket) => (
              <div key={ticket.chat_id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`ticket-${ticket.chat_id}`}>
                <button onClick={() => handleSelectChat(ticket)} className="flex-1 flex items-center gap-4 text-left min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${ticket.status === "open" || ticket.status === "pending" ? "bg-emerald-100" : "bg-slate-200"}`}>
                    <MessageCircle className={`w-4 h-4 ${ticket.status === "open" || ticket.status === "pending" ? "text-emerald-600" : "text-slate-500"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 truncate text-sm">{ticket.user_name || "Guest"}</p>
                    <p className="text-xs text-slate-500 truncate">{ticket.last_message || "No messages"}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticket.status === "open" || ticket.status === "pending" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{ticket.status}</span>
                    <p className="text-xs text-slate-400 mt-1">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}</p>
                  </div>
                </button>
                <button onClick={(e) => handleDeleteChat(ticket.chat_id, e)} className="p-2 text-slate-300 hover:text-rose-500 transition-colors shrink-0" data-testid={`delete-ticket-${ticket.chat_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
